/**
 * Browser Notification reminder service
 * Schedules browser push notifications for calendar events
 * - 30 minutes before
 * - 5 minutes before
 * Uses the Web Notifications API + setTimeout
 */

import type { CalendarEvent } from '../types'

// Store active timeout IDs so we can cancel them
const reminderTimers = new Map<string, ReturnType<typeof setTimeout>[]>()

// ── Permission ─────────────────────────────────────────────────
export async function requestNotificationPermission(): Promise<boolean> {
  if (!('Notification' in window)) return false
  if (Notification.permission === 'granted') return true
  if (Notification.permission === 'denied') return false
  const perm = await Notification.requestPermission()
  return perm === 'granted'
}

export function notificationsSupported(): boolean {
  return 'Notification' in window
}

export function notificationsGranted(): boolean {
  return 'Notification' in window && Notification.permission === 'granted'
}

// ── Show a notification ────────────────────────────────────────
function showNotification(title: string, body: string, tag: string) {
  if (!notificationsGranted()) return
  try {
    const n = new Notification(title, {
      body,
      tag,
      icon: '/favicon.svg',
      badge: '/favicon.svg',
      requireInteraction: false,
    })
    // Auto-close after 8 seconds
    setTimeout(() => n.close(), 8000)
  } catch {
    // Silently fail (e.g. in some browser contexts)
  }
}

// ── Schedule reminders for one event ──────────────────────────
export function scheduleReminders(ev: CalendarEvent) {
  if (!ev.time || !ev.date) return  // skip all-day events

  cancelReminders(ev.id)  // clear any existing

  const eventMs = new Date(`${ev.date}T${ev.time}:00`).getTime()
  if (isNaN(eventMs)) return

  const now = Date.now()
  const timers: ReturnType<typeof setTimeout>[] = []

  const ALERTS = [
    { minsBefore: 30, label: 'in 30 minutes' },
    { minsBefore: 5,  label: 'in 5 minutes' },
    { minsBefore: 0,  label: 'now' },
  ]

  for (const { minsBefore, label } of ALERTS) {
    const fireAt = eventMs - minsBefore * 60_000
    const delay  = fireAt - now
    if (delay < 0) continue  // already past

    const clientPart = ev.clientName ? ` · ${ev.clientName}` : ''
    const title = minsBefore === 0 ? `🔔 Starting now: ${ev.title}` : `⏰ ${ev.title} ${label}`
    const body  = `${ev.time}${clientPart}`

    const tid = setTimeout(
      () => showNotification(title, body, `erp-ev-${ev.id}-${minsBefore}`),
      delay,
    )
    timers.push(tid)
  }

  if (timers.length > 0) {
    reminderTimers.set(ev.id, timers)
  }
}

// ── Cancel reminders for one event ────────────────────────────
export function cancelReminders(eventId: string) {
  const timers = reminderTimers.get(eventId)
  if (timers) {
    timers.forEach(clearTimeout)
    reminderTimers.delete(eventId)
  }
}

// ── Bulk schedule (call on page mount) ────────────────────────
export function scheduleAllReminders(events: CalendarEvent[]) {
  for (const ev of events) {
    scheduleReminders(ev)
  }
}

// ── Cancel all ────────────────────────────────────────────────
export function cancelAllReminders() {
  reminderTimers.forEach(timers => timers.forEach(clearTimeout))
  reminderTimers.clear()
}
